import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { UserMenu } from '../components/UserMenu';
import { User, Mail, CreditCard, Shield, Calendar, ChevronRight, Edit3 } from 'lucide-react';
import { BackgroundPattern } from '../components/ui/background-pattern';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  // Format the created_at date from the user object
  const joinDate = user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'May 2025';
  
  // We don't need to call refreshUserData on mount
  // The user data should already be available from the AuthContext
  // This prevents infinite API calls to /api/auth/me
  
  return (
    <div className="relative min-h-screen">
      <BackgroundPattern />
      <div className="relative">
        {/* Custom header */}
        <header className="fixed top-0 left-0 right-0 bg-transparent backdrop-blur-sm border-b border-gray-200/10 dark:border-gray-800/10 z-50">
          <div className="container max-w-screen-2xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-8">
              <Link 
                to="/dashboard" 
                className="flex items-center text-xl font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
              >
                <img src="/i-can-pitch-logo.jpg" alt="Logo" className="h-8 w-auto mr-2" />
                ICanPitch
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <UserMenu showLogout={true} />
            </div>
          </div>
        </header>
        
        <main className="min-h-screen bg-transparent">
          <div className="container mx-auto py-8 px-4 pt-24 pb-12">
            <h1 className="text-5xl font-extrabold mb-6 tracking-tight text-center">
              <span className="bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-400 bg-clip-text text-transparent">
                Your Profile
              </span>
            </h1>
            
            <div className="flex flex-col items-center">
              <div className="w-full max-w-3xl mx-auto">
                {/* Profile Header Card */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200/50 dark:border-gray-700/50 p-8 mb-6 relative overflow-hidden">
            {/* Background Pattern removed */}
            
            <div className="flex flex-col md:flex-row items-center gap-8 relative z-10">
              {/* Profile Photo */}
              <div className="relative group">
                <div className="w-36 h-36 rounded-full bg-gradient-to-br from-indigo-100 to-indigo-50 dark:from-indigo-900/30 dark:to-indigo-800/20 p-1 shadow-lg">
                  <div className="w-full h-full rounded-full bg-white dark:bg-gray-800 flex items-center justify-center overflow-hidden">
                    {user?.profile_picture ? (
                      <img
                        src={user.profile_picture}
                        alt="Profile"
                        className="w-full h-full object-cover"
                        crossOrigin="anonymous"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <span className="text-indigo-500 dark:text-indigo-400 text-6xl font-semibold">
                        {user?.name?.charAt(0) || '?'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Profile Info */}
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {user?.name || 'User'}
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {user?.email || 'No email provided'}
                </p>
                
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <div className="flex items-center bg-indigo-50 dark:bg-indigo-900/20 px-4 py-2 rounded-full">
                    <CreditCard className="h-4 w-4 text-indigo-600 dark:text-indigo-400 mr-2" />
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{user?.credits || 0} credits</span>
                  </div>
                  
                  <div className="flex items-center bg-emerald-50 dark:bg-emerald-900/20 px-4 py-2 rounded-full">
                    <Calendar className="h-4 w-4 text-emerald-600 dark:text-emerald-400 mr-2" />
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Joined {joinDate}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
                {/* Profile Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Left Column - Personal Information */}
                  <div className="md:col-span-2">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
                      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Personal Information</h3>
                      </div>
                
                      <div className="p-6 space-y-6">
                  {isEditing ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Full Name</label>
                        <input
                          type="text"
                          defaultValue={user?.name || ''}
                          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Your full name"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Address</label>
                        <input
                          type="email"
                          defaultValue={user?.email || ''}
                          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Your email address"
                          disabled
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Email cannot be changed</p>
                      </div>
                      
                      <div className="pt-4">
                        <button className="w-full md:w-auto px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors shadow-sm">
                          Save Changes
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center py-3 border-b border-gray-100 dark:border-gray-700">
                        <div className="w-1/3">
                          <div className="flex items-center">
                            <User className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2" />
                            <span className="text-gray-600 dark:text-gray-400">Full Name</span>
                          </div>
                        </div>
                        <div className="w-2/3">
                          <span className="font-medium text-gray-900 dark:text-white">{user?.name || 'Not provided'}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center py-3 border-b border-gray-100 dark:border-gray-700">
                        <div className="w-1/3">
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2" />
                            <span className="text-gray-600 dark:text-gray-400">Email</span>
                          </div>
                        </div>
                        <div className="w-2/3">
                          <span className="font-medium text-gray-900 dark:text-white">{user?.email || 'Not provided'}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center py-3">
                        <div className="w-1/3">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2" />
                            <span className="text-gray-600 dark:text-gray-400">Joined</span>
                          </div>
                        </div>
                        <div className="w-2/3">
                          <span className="font-medium text-gray-900 dark:text-white">{joinDate}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
                  {/* Right Column - Account & Billing */}
                  <div className="space-y-6">
                    {/* Account Summary */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
                      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Account Summary</h3>
                      </div>
                      
                      <div className="p-6">
                  <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center">
                      <CreditCard className="h-5 w-5 text-indigo-500 dark:text-indigo-400 mr-3" />
                      <span className="text-gray-700 dark:text-gray-300">Credits</span>
                    </div>
                    <span className="font-semibold text-gray-900 dark:text-white">{user?.credits || 0}</span>
                  </div>
                  
                  <div className="flex items-center justify-between py-3">
                    <div className="flex items-center">
                      <Shield className="h-5 w-5 text-indigo-500 dark:text-indigo-400 mr-3" />
                      <span className="text-gray-700 dark:text-gray-300">Account Type</span>
                    </div>
                    <span className="font-semibold text-gray-900 dark:text-white">Standard</span>
                  </div>
                  
                        <Link 
                          to="/billing"
                          className="flex items-center justify-between mt-4 px-4 py-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
                        >
                          <span className="font-medium">Manage Billing</span>
                          <ChevronRight size={18} />
                        </Link>
                </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Profile;
